import Tracking from '@vromo/analytics';
import useViewModel from '@vromo/hooks/useViewModel';
import { useStore } from '@vromo/mobx-react';
import { Contact } from '@vromo/models';
import Button from '@vromo/ui/components/Button';
import Container from '@vromo/ui/components/Container';
import Input from '@vromo/ui/components/Input';
import RadioGroup from '@vromo/ui/components/Radio';
import { useToasts } from '@vromo/ui/components/Toasts';
import { passwordCheck } from '@vromo/ui/helpers';
import { action, runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { update } from 'serializr';
import ReactTelInput from '../../../common/components/ControlledTelInput/ControlledTelInput';
import { RootStore } from '../../../stores/RootStore';
import styles from './accountSettings.style';
import BetaSettings from './components/BetaSettings';
import ChangePassword from './components/ChangePassword';
import ProfilePictureEditor from './components/ProfilePictureEditor';
import Errors from './components/Errors';

const MemoInput = memo(Input);
const MemoContainer = memo(Container);
const MemoPhoneInput = memo(ReactTelInput);
const MemoRadioGroup = memo(RadioGroup);

const inputStyles = { marginBottom: 15 };

const distanceOptions = [
    {
        label: 'Metric',
        value: 'false'
    },
    { label: 'Imperial', value: 'true' }
];

const AccountSettings = observer(() => {
    const { t } = useTranslation();
    const translate = (key: string, params?: any) => t(`settings.${key}`, params);
    const {
        authStore: { graph, api, user, token },
        viewStore
    } = useStore<RootStore>('rootStore');

    const showToast = useToasts();

    const store = useLocalObservable<{
        errors: Array<any>;
        oldPassword: string;
        newPassword: string;
        confirmPassword: string;
        clearPasswords: () => void;
    }>(() => ({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
        errors: [],
        clearPasswords() {
            this.oldPassword = '';
            this.newPassword = '';
            this.confirmPassword = '';
        }
    }));

    const [contact, contactPatch] = useViewModel(user.contact || new Contact());
    const [isSavingContact, setSavingContact] = useState<boolean>(false);
    const [isSavingPassword, setSavingPassword] = useState<boolean>(false);

    const updateContact = async () => {
        setSavingContact(true);
        store.errors = store.errors.filter((error) => error.field !== 'contact');

        try {
            const patch = await graph.root.graph().user().contact().post(contactPatch);

            update(user.contact, { ...patch });

            store.errors.push({
                field: 'contact',
                error: false,
                message: translate('contactsUpdated')
            });

            Tracking.track('Contact Details Updated');
        } catch (e) {
            console.error(e);
            showToast(translate('contactUpdateError'), 'error');
        } finally {
            setSavingContact(false);
        }
    };

    const updatePassword = useCallback(async () => {
        setSavingPassword(true);
        const { oldPassword, newPassword, confirmPassword } = store;

        store.errors = store.errors.filter((error) => error.field !== 'password');

        if (oldPassword && !oldPassword.length) {
            store.errors.push({
                field: 'password',
                error: true,
                message: translate('enterOldPassword')
            });
        }

        store.errors.push(...passwordCheck(newPassword, confirmPassword));

        if (store.errors.length || !token) {
            setSavingPassword(false);
            return;
        }

        api.changePassword
            .post({ existing: oldPassword, password: newPassword }, undefined, token)
            .then(() => {
                runInAction(() => {
                    Tracking.track('Password Changed');
                    store.errors.push({
                        field: 'password',
                        error: false,
                        message: translate('passwordUpdated')
                    });

                    store.clearPasswords();
                });
                setSavingPassword(false);
            })
            .catch(({ response: { status_code } }) => {
                const error = { field: 'password', error: true, message: '' };

                if (status_code === 400) {
                    store.errors.push({ ...error, message: translate('strongerPassword') });
                }
                if (status_code === 403) {
                    store.errors.push({ ...error, message: translate('incorrectPassword') });
                }
                setSavingPassword(false);
            });
    }, [token, store.oldPassword, store.newPassword, store.confirmPassword, store.errors]);

    const onChangeName = useCallback((e) => update(contact, { name: e.target.value }), [contact]);
    const onChangeEmail = useCallback((e) => update(contact, { email: e.target.value }), [contact]);
    const onChangePhone = useCallback(
        (phone: string) => update(contact, { phone: phone.replace(/\s/, '') }),
        [contact]
    );

    const { errors, oldPassword, newPassword, confirmPassword } = store;
    const onChangeOldPassword = useCallback((e) => (store.oldPassword = e.target.value), []);
    const onChangeNewPassword = useCallback((e) => (store.newPassword = e.target.value), []);
    const onChangeConfirmPassword = useCallback(
        (e) => (store.confirmPassword = e.target.value),
        []
    );

    const onChangeDistanceUnits = useCallback(
        action((e) => (viewStore.imperialUnits = e.target.value === 'true')),
        []
    );

    return (
        <div className={styles.container}>
            <div className={styles.contentContainer}>
                <MemoContainer title={translate('profilePicture')}>
                    <ProfilePictureEditor />
                </MemoContainer>
                <MemoContainer title={translate('contactDetails')}>
                    <div>
                        <Errors errors={store.errors} field="contact" />
                        <MemoInput
                            type="text"
                            label={translate('name')}
                            placeholder={translate('name')}
                            style={inputStyles}
                            value={contact.name || ''}
                            onChange={onChangeName}
                        />
                        <MemoInput
                            type="text"
                            label={translate('email')}
                            placeholder={translate('email')}
                            style={inputStyles}
                            value={contact.email || ''}
                            onChange={onChangeEmail}
                        />
                        <div className={styles.formGroup}>
                            <label>{translate('phone')}</label>
                            <MemoPhoneInput
                                value={contact.phone || ''}
                                default={viewStore.callingCode}
                                defaultCountry={viewStore.country}
                                onChange={onChangePhone}
                            />
                        </div>
                        <div className={styles.submitButtonContainer}>
                            <Button
                                onClick={updateContact}
                                disabled={isSavingContact}
                                loading={isSavingContact}>
                                {translate('save')}
                            </Button>
                        </div>
                    </div>
                </MemoContainer>
                <MemoContainer title={translate('distanceUnits')}>
                    <div>
                        <MemoRadioGroup
                            options={distanceOptions}
                            direction="row"
                            onChange={onChangeDistanceUnits}
                            checked={viewStore.imperialUnits?.toString()}
                        />
                    </div>
                </MemoContainer>
                <ChangePassword
                    {...{
                        errors,
                        oldPassword,
                        newPassword,
                        confirmPassword,
                        onChangeOldPassword,
                        onChangeNewPassword,
                        onChangeConfirmPassword,
                        updatePassword,
                        isSavingPassword
                    }}
                />
                <BetaSettings />
            </div>
        </div>
    );
});

export default AccountSettings;
